import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Zap, Loader2, CheckCircle, Paperclip, File, Trash2, Upload, Mail, Lock } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import DOMPurify from 'dompurify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WattsonChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [contractSubmitted, setContractSubmitted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]); // entries: { file, thumbUrl }
  const [isDragging, setIsDragging] = useState(false);
  const [visitorEmail, setVisitorEmail] = useState(() => localStorage.getItem('wattson_visitor_email') || '');
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const previewUrlsRef = useRef([]); // Object URLs created during this session

  // Revoke all created object URLs once, on unmount only
  useEffect(() => {
    const urls = previewUrlsRef.current;
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Show popup after 3 seconds if chat hasn't been opened
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isOpen && !popupDismissed) {
        setShowPopup(true);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isOpen, popupDismissed]);

  // Auto-hide popup after 15 seconds
  useEffect(() => {
    if (showPopup) {
      const hideTimer = setTimeout(() => {
        setShowPopup(false);
      }, 15000);
      return () => clearTimeout(hideTimer);
    }
  }, [showPopup]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Parse contract request from Wattson's response
  const parseContractRequest = (response) => {
    const contractMatch = response.match(/\[CONTRACT_REQUEST\]([\s\S]*?)\[\/CONTRACT_REQUEST\]/);
    if (!contractMatch) return null;

    const contractData = {};
    const lines = contractMatch[1].trim().split('\n');
    
    lines.forEach(line => {
      const [key, ...valueParts] = line.split(':');
      if (key && valueParts.length > 0) {
        const value = valueParts.join(':').trim();
        contractData[key.trim()] = value;
      }
    });

    return contractData;
  };

  // Handle file selection with validation
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
  const ALLOWED_TYPES = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'image/webp'];
  
  // Reusable file validation function
  const validateAndAddFiles = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];
    
    fileArray.forEach(file => {
      // Check file type
      if (!ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Only PDF and image files are allowed`);
        return;
      }
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File size must be under 5MB`);
        return;
      }
      validFiles.push(file);
    });
    
    // Show errors if any
    if (errors.length > 0) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: `Some files couldn't be attached:\n${errors.join('\n')}`
      }]);
    }
    
    if (validFiles.length > 0) {
      // Create one preview URL per image, reused everywhere and revoked on unmount
      const newEntries = validFiles.map(f => {
        const thumbUrl = f.type.startsWith('image/') ? URL.createObjectURL(f) : null;
        if (thumbUrl) previewUrlsRef.current.push(thumbUrl);
        return { file: f, thumbUrl };
      });
      setUploadedFiles(prev => [...prev, ...newEntries]);

      // Add a message showing files were attached with previews
      setMessages(prev => [...prev, {
        role: 'user',
        content: `Attached: ${validFiles.map(f => f.name).join(', ')}`,
        isFileAttachment: true,
        filePreviews: newEntries.map(entry => ({
          name: entry.file.name,
          type: entry.file.type,
          previewUrl: entry.thumbUrl,
        }))
      }]);
    }
  };

  const handleFileSelect = (e) => {
    validateAndAddFiles(e.target.files);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Remove uploaded file
  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging to false if we're leaving the drop zone entirely
    if (e.currentTarget === dropZoneRef.current && !e.currentTarget.contains(e.relatedTarget)) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      validateAndAddFiles(files);
    }
  };

  // Submit contract request with files to backend
  const submitContractRequest = async (contractData) => {
    try {
      const formData = new FormData();
      
      // Add contract data
      formData.append('name', contractData.name || '');
      formData.append('email', contractData.email || '');
      formData.append('phone', contractData.phone || '');
      formData.append('business_name', contractData.business_name || '');
      formData.append('account_number', contractData.account_number || '');
      formData.append('energy_type', contractData.energy_type || '');
      formData.append('state', contractData.state || '');
      formData.append('annual_usage', contractData.annual_usage || '');
      formData.append('current_rate', contractData.current_rate || '');
      formData.append('offer_rate', contractData.offer_rate || '');
      formData.append('savings_percent', contractData.savings_percent || '');
      formData.append('annual_savings', contractData.annual_savings || '');
      formData.append('session_id', sessionId || '');
      
      // Add uploaded files
      uploadedFiles.forEach(entry => {
        formData.append('files', entry.file);
      });

      await axios.post(`${BACKEND_URL}/api/submit-contract-with-files`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setContractSubmitted(true);
      setUploadedFiles([]); // Clear files after submission
      return true;
    } catch (error) {
      console.error('Failed to submit contract:', error);
      return false;
    }
  };

  // Remove contract request block from message for display
  const cleanMessageForDisplay = (response) => {
    return response.replace(/\[CONTRACT_REQUEST\][\s\S]*?\[\/CONTRACT_REQUEST\]/g, '').trim();
  };

  const startNewChat = async () => {
    setIsInitializing(true);
    setContractSubmitted(false);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat/start`);
      setSessionId(response.data.session_id);
      const savedEmail = localStorage.getItem('wattson_visitor_email');
      if (savedEmail) {
        axios.post(`${BACKEND_URL}/api/chat/capture-lead`, { email: savedEmail, session_id: response.data.session_id }).catch(() => {});
      }
      setMessages([
        {
          role: 'assistant',
          content: response.data.greeting
        }
      ]);
    } catch (error) {
      console.error('Failed to start chat:', error);
      setMessages([
        {
          role: 'assistant',
          content: "Hi there! I'm Wattson, your energy savings specialist from Last Mile Advisors. I'm having a bit of trouble connecting right now, but feel free to email us at sales@lastmileadvisors.com and we'll help you save on your energy bills!"
        }
      ]);
    }
    setIsInitializing(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setShowPopup(false);
    setPopupDismissed(true);
    if (visitorEmail && messages.length === 0) {
      startNewChat();
    }
  };

  const handleEmailGateSubmit = async (e) => {
    e.preventDefault();
    const email = emailInput.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    setIsSubmittingEmail(true);
    try {
      await axios.post(`${BACKEND_URL}/api/chat/capture-lead`, { email });
    } catch (error) {
      console.error('Lead capture failed:', error);
    }
    localStorage.setItem('wattson_visitor_email', email);
    setVisitorEmail(email);
    setIsSubmittingEmail(false);
    startNewChat();
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const dismissPopup = (e) => {
    e.stopPropagation();
    setShowPopup(false);
    setPopupDismissed(true);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message to UI
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/chat`, {
        session_id: sessionId,
        message: userMessage
      });
      
      const fullResponse = response.data.response;
      
      // Check if there's a contract request in the response
      const contractData = parseContractRequest(fullResponse);
      if (contractData && contractData.email) {
        // Submit contract in background
        submitContractRequest(contractData);
      }
      
      // Clean the message for display (remove contract block)
      const displayMessage = cleanMessageForDisplay(fullResponse);
      
      // Add assistant response
      setMessages(prev => [...prev, { role: 'assistant', content: displayMessage }]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: "I apologize, but I'm having trouble processing that. Please try again or reach out to us directly at sales@lastmileadvisors.com." 
      }]);
    }
    setIsLoading(false);
  };

  const formatMessage = (content) => {
    // Handle markdown-like formatting
    let formatted = content
      // Bold text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Line breaks
      .split('\n')
      .map((line) => {
        // Handle bullet points
        if (line.trim().startsWith('- ') || line.trim().startsWith('• ')) {
          return `<li>${line.replace(/^[-•]\s*/, '')}</li>`;
        }
        // Handle numbered lists
        if (/^\d+\.\s/.test(line.trim())) {
          return `<li>${line.replace(/^\d+\.\s*/, '')}</li>`;
        }
        return line;
      })
      .join('<br/>');
    
    return formatted;
  };

  return (
    <>
      {/* Popup Message */}
      {showPopup && !isOpen && (
        <div className="wattson-popup" onClick={handleOpen}>
          <button className="wattson-popup-close" onClick={dismissPopup} aria-label="Dismiss">
            <X size={14} />
          </button>
          <div className="wattson-popup-content">
            <Zap size={20} className="wattson-popup-icon" />
            <div className="wattson-popup-text">
              <strong>Save 10-30% on your energy bills!</strong>
              <span>Chat with Wattson to see your savings</span>
            </div>
          </div>
        </div>
      )}

      {/* Chat Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="wattson-chat-button"
          aria-label="Open chat with Wattson"
        >
          <div className="wattson-button-content">
            <Zap size={24} className="wattson-icon" />
            <span className="wattson-button-text">Chat with Wattson</span>
          </div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`wattson-chat-window ${isDragging ? 'wattson-dragging' : ''}`}
          ref={dropZoneRef}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {/* Drag Overlay */}
          {isDragging && (
            <div className="wattson-drag-overlay" data-testid="wattson-drag-overlay">
              <div className="wattson-drag-content">
                <Upload size={48} />
                <span>Drop your utility bill here</span>
                <span className="wattson-drag-hint">PDF or images up to 5MB</span>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="wattson-header">
            <div className="wattson-header-info">
              <div className="wattson-avatar">
                <Zap size={20} />
              </div>
              <div className="wattson-header-text">
                <span className="wattson-name">Wattson</span>
                <span className="wattson-status">Energy Savings Specialist</span>
              </div>
            </div>
            <button onClick={handleClose} className="wattson-close-btn" aria-label="Close chat">
              <X size={20} />
            </button>
          </div>

          {/* Email Gate */}
          {!visitorEmail ? (
            <div className="wattson-email-gate" data-testid="wattson-email-gate">
              <div className="wattson-gate-icon">
                <Zap size={28} />
              </div>
              <h3 className="wattson-gate-title">Chat with Wattson</h3>
              <p className="wattson-gate-subtitle">
                Enter your email to start chatting with our energy savings specialist. See how much you could save in under 2 minutes.
              </p>
              <form onSubmit={handleEmailGateSubmit} className="wattson-gate-form">
                <div className="wattson-gate-input-wrap">
                  <Mail size={16} className="wattson-gate-input-icon" />
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => { setEmailInput(e.target.value); setEmailError(''); }}
                    placeholder="you@company.com"
                    className="wattson-gate-input"
                    autoFocus
                    data-testid="wattson-email-input"
                  />
                </div>
                {emailError && (
                  <span className="wattson-gate-error" data-testid="wattson-email-error">{emailError}</span>
                )}
                <Button
                  type="submit"
                  className="wattson-gate-btn"
                  disabled={!emailInput.trim() || isSubmittingEmail}
                  data-testid="wattson-email-submit-btn"
                >
                  {isSubmittingEmail ? <Loader2 size={16} className="animate-spin" /> : 'Start Chatting'}
                </Button>
              </form>
              <div className="wattson-gate-privacy">
                <Lock size={11} />
                <span>We respect your inbox. No spam, ever.</span>
              </div>
            </div>
          ) : (
          <>
          {/* Contract Submitted Banner */}
          {contractSubmitted && (
            <div className="wattson-contract-banner">
              <CheckCircle size={16} />
              <span>Contract request submitted!</span>
            </div>
          )}

          {/* Messages */}
          <div className="wattson-messages">
            {isInitializing ? (
              <div className="wattson-loading-initial">
                <Loader2 size={24} className="animate-spin" />
                <span>Wattson is connecting...</span>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  className={`wattson-message ${msg.role === 'user' ? 'wattson-user' : 'wattson-assistant'}`}
                  data-testid={`chat-message-${index}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="wattson-message-avatar">
                      <Zap size={14} />
                    </div>
                  )}
                  <div className="wattson-message-content">
                    {/* Show image previews for file attachments */}
                    {msg.filePreviews && msg.filePreviews.length > 0 && (
                      <div className="wattson-message-previews" style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginBottom: '0.5rem'
                      }}>
                        {msg.filePreviews.map((file, i) => (
                          file.previewUrl ? (
                            <div key={i} style={{
                              width: 80,
                              height: 80,
                              borderRadius: 8,
                              overflow: 'hidden',
                              border: '1px solid rgba(255,255,255,0.2)',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                              <img 
                                src={file.previewUrl}
                                alt={file.name}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                              />
                            </div>
                          ) : (
                            <div key={i} style={{
                              width: 80,
                              height: 80,
                              borderRadius: 8,
                              background: 'rgba(255,255,255,0.1)',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(255,255,255,0.2)',
                              padding: '0.25rem'
                            }}>
                              <File size={24} style={{ opacity: 0.7, marginBottom: 4 }} />
                              <span style={{ 
                                fontSize: '0.55rem', 
                                opacity: 0.8,
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                width: '100%'
                              }}>
                                {file.name.length > 12 ? file.name.slice(0, 10) + '...' : file.name}
                              </span>
                            </div>
                          )
                        ))}
                      </div>
                    )}
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatMessage(msg.content)) }} />
                  </div>
                </div>
              ))
            )}
            {isLoading && (
              <div className="wattson-message wattson-assistant">
                <div className="wattson-message-avatar">
                  <Zap size={14} />
                </div>
                <div className="wattson-typing">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Uploaded Files Preview */}
          {uploadedFiles.length > 0 && (
            <div className="wattson-files-preview">
              {uploadedFiles.map((entry, index) => {
                return (
                  <div key={`${entry.file.name}-${index}`} className="wattson-file-item" data-testid={`file-preview-${index}`}>
                    {entry.thumbUrl ? (
                      <div className="wattson-file-thumbnail">
                        <img 
                          src={entry.thumbUrl} 
                          alt={entry.file.name}
                          style={{
                            width: 40,
                            height: 40,
                            objectFit: 'cover',
                            borderRadius: 4,
                          }}
                        />
                      </div>
                    ) : (
                      <File size={14} />
                    )}
                    <span className="wattson-file-name">{entry.file.name}</span>
                    <button 
                      onClick={() => removeFile(index)} 
                      className="wattson-file-remove"
                      aria-label="Remove file"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Input */}
          <form onSubmit={handleSendMessage} className="wattson-input-form">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              multiple
              accept=".pdf,.png,.jpg,.jpeg,.webp,image/*,application/pdf"
              style={{ display: 'none' }}
              data-testid="wattson-file-input"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="wattson-attach-btn"
              aria-label="Attach file"
              disabled={isLoading || isInitializing}
              data-testid="wattson-attach-btn"
            >
              <Paperclip size={18} />
            </button>
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Type your message..."
              className="wattson-input"
              disabled={isLoading || isInitializing}
            />
            <Button
              type="submit"
              className="wattson-send-btn"
              disabled={!inputValue.trim() || isLoading || isInitializing}
            >
              <Send size={18} />
            </Button>
          </form>
          </>
          )}
        </div>
      )}
    </>
  );
};

export default WattsonChat;

